// Get API host from config
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios"

import { useAuthStore } from "@/modules/helpers/hooks/shared"
import { sendLogout, sendUpdateTokenFromWeb } from "@/modules/helpers/utils"

const APP_API_HOST = import.meta.env.VITE_APP_API_HOST

export const newEventSource = (
  url: string,
  accessToken: string,
  onmessage: (type: any, data: any) => void,
  onerror?: (error: any) => void
): { close: () => void } => {
  if (!accessToken) return { close: () => 0 }
  const api = `${url}?access_token=${accessToken}`

  let isCancelled = false
  let reader: ReadableStreamDefaultReader | null = null
  let controller: AbortController
  let reconnectTimeout: NodeJS.Timeout | null = null

  const reconnectDelay = 3000
  const reconnect = () => {
    if (!isCancelled) {
      controller?.abort()
      reader?.cancel().catch(() => {})
      reconnectTimeout = setTimeout(fetchStream, reconnectDelay)
    }
  }

  const fetchStream = async () => {
    controller = new AbortController()
    try {
      const response = await fetch(api, {
        headers: {
          "Content-Type": "text/event-stream",
          Accept: "text/event-stream",
          Connection: "keep-alive"
        },
        signal: controller.signal
      })

      if (!response.ok) throw new Error("Failed to connect to server")

      reader = response.body!.getReader()
      const decoder = new TextDecoder()
      let done = false
      let buffer = ""
      while (!done && !isCancelled) {
        const { value, done: readerDone } = await reader.read()
        done = readerDone

        buffer += decoder.decode(value || new Uint8Array(), { stream: true })

        const events = buffer.split("\n\n")
        buffer = events.pop() || ""

        for (const eventText of events) {
          try {
            const matchEvent = /event:\s*(\w+)\s*/.exec(eventText)
            const matchData = /data:\s*(\{[\s\S]*\})/.exec(eventText)

            if (matchEvent && matchData) {
              const type = matchEvent[1]
              const data = JSON.parse(matchData[1])
              onmessage(type, data)
            }
          } catch (err) {
            onerror?.(err)
          }
        }
      }
    } catch (err) {
      // check 401
      if (err instanceof AxiosError && err.response?.status === 401) {
        onerror?.(err)
      }

      if (!isCancelled) {
        onerror?.(err)
        reconnect()
      }
    }
  }

  fetchStream()

  return {
    close: () => {
      isCancelled = true
      if (reconnectTimeout) clearTimeout(reconnectTimeout)
      controller?.abort()
      reader?.cancel().catch(() => {})
    }
  }
}

export interface SchemaResponse<T> {
  items: any[]
  data: T
  message: string
  code: number
}

// Axios instance factory
const createAxiosInstance = (baseURL: string): AxiosInstance =>
  axios.create({
    baseURL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  })

// Axios instances
const axiosAuth = createAxiosInstance(APP_API_HOST)
const axiosAuthOneTime = createAxiosInstance(APP_API_HOST)
const axiosClient = createAxiosInstance(APP_API_HOST)

// Logout utility
export const signOut = (): void => {
  const { logout } = useAuthStore.getState()
  logout()
  sendLogout()
  window.dispatchEvent(new Event("unauthorized"))
}

export interface ILoginResponse {
  accessToken?: string
  refreshToken?: string
  expiration?: string
  isNewUser?: boolean
}

// Singleton promise để tránh race condition
let refreshPromise: Promise<string | null> | null = null

// Token refresh logic - Optimized với singleton pattern
export const refreshAuthorize = async (): Promise<string | null> => {
  // Nếu đang refresh, chờ kết quả từ promise hiện tại
  if (refreshPromise) {
    return refreshPromise
  }

  const { refreshToken } = useAuthStore.getState()
  if (!refreshToken) {
    signOut()
    return null
  }

  // Tạo và lưu promise
  refreshPromise = (async () => {
    try {
      const { data } = await unAuth.post<SchemaResponse<ILoginResponse>>("auth/refresh-token", {
        refreshToken
      })

      const { accessToken: newAccessToken, refreshToken: newRefreshToken } = data?.data || {}

      if (!newAccessToken || !newRefreshToken) {
        signOut()
        return null
      }

      const payload = {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken
      }

      // Update local store immediately
      useAuthStore.getState().login(payload)

      // Sync with native app
      sendUpdateTokenFromWeb(payload)

      return newAccessToken
    } catch (error) {
      if (error instanceof AxiosError) {
        // Only logout if it's an auth error (refresh token expired/invalid)
        if (error.response?.status === 401 || error.response?.status === 403) {
          signOut()
        }
        // For other errors (network, 500, etc), do nothing (user stays logged in, request fails)
      } else {
        // Unknown error verify
        console.error("Token refresh error:", error)
      }
      return null
    } finally {
      // Luôn reset promise dù thành công hay thất bại
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// Auth interceptor factory
const createAuthInterceptors = (
  axiosInstance: AxiosInstance,
  getToken: () => string | null
): AxiosInstance => {
  // Request interceptor
  axiosInstance.interceptors.request.use(
    (config: AxiosRequestConfig): any => {
      const accessToken = getToken()

      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
          Accept: "application/json",
          "Content-Type": "application/json"
        }
      }
    },
    (error: AxiosError) => Promise.reject(error as AxiosError<SchemaResponse<null>>)
  )

  // Response interceptor with token refresh
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true
        const accessToken = await refreshAuthorize()
        if (!accessToken) return Promise.reject(error)

        axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`

        // Explicitly update the header for the retry
        if (!originalRequest.headers) {
          originalRequest.headers = {}
        }
        originalRequest.headers.Authorization = `Bearer ${accessToken}`

        return axiosInstance(originalRequest)
      }
      return Promise.reject(error)
    }
  )

  return axiosInstance
}

// Public API clients
export const unAuth = axiosClient

// Auth API clients with interceptors
export const auth = createAuthInterceptors(axiosAuth, () => useAuthStore.getState().accessToken)
export const authOneTime = createAuthInterceptors(
  axiosAuthOneTime,
  () => useAuthStore.getState().accessToken
)
